// Shared buyer-returns error states (screens-buyer-app §24). One Alert-based
// presentational component + named preset helpers for the four spec cases:
//   network failure, RET-005 (active return exists), RET-011 (optimistic lock),
//   RET-110 (permission denied).
// No emoji — the spec's warning/lock glyphs map to Phosphor icons.
// These render INLINE (persistent panels). ReturnApi does NOT auto-toast — a
// catch that needs a transient message uses message.error; these are the panels.
import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import {
  HourglassMediumIcon,
  type Icon,
  LockKeyIcon,
  WarningCircleIcon,
  WifiSlashIcon,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

export interface ReturnErrorAction {
  label: string;
  onClick: () => void;
  variant?: "filled" | "light" | "default" | "outline" | "subtle";
}

export interface ReturnErrorStateProps {
  title: string;
  message?: string;
  /** Phosphor icon; defaults to a warning circle. */
  icon?: Icon;
  /** Mantine color (semantic). Defaults to "red". */
  color?: string;
  actions?: ReturnErrorAction[];
}

export function ReturnErrorState({
  title,
  message,
  icon: IconCmp = WarningCircleIcon,
  color = "red",
  actions = [],
}: ReturnErrorStateProps) {
  return (
    <Alert color={color} variant="light" radius="md" icon={<IconCmp size={20} />} title={title}>
      <Stack gap="sm">
        {message ? <Text size="sm">{message}</Text> : null}
        {actions.length ? (
          <Group gap="sm">
            {actions.map((a) => (
              <Button
                key={a.label}
                size="sm"
                color={color}
                variant={a.variant ?? "light"}
                onClick={a.onClick}
              >
                {a.label}
              </Button>
            ))}
          </Group>
        ) : null}
      </Stack>
    </Alert>
  );
}

// ── Preset helpers (props-only; callers supply the handlers) ─────────────────

// Network failure (§24).
export function ReturnNetworkError({
  onRetry,
  onClose,
}: {
  onRetry?: () => void;
  onClose?: () => void;
}) {
  const { t } = useTranslation("buyerApp");
  const actions: ReturnErrorAction[] = [];
  if (onRetry) actions.push({ label: t("errorState.networkRetry"), onClick: onRetry, variant: "filled" });
  if (onClose) actions.push({ label: t("errorState.networkClose"), onClick: onClose, variant: "default" });
  return (
    <ReturnErrorState
      icon={WifiSlashIcon}
      color="red"
      title={t("errorState.networkTitle")}
      message={t("errorState.networkMessage")}
      actions={actions}
    />
  );
}

// RET-005 — an active return already exists for the item (§24).
export function ReturnActiveExistsError({
  productName,
  returnNumber,
  onGoToReturn,
  onCancel,
}: {
  productName?: string;
  returnNumber?: string;
  onGoToReturn?: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation("buyerApp");
  const actions: ReturnErrorAction[] = [];
  if (onGoToReturn) {
    actions.push({
      label: returnNumber ? t("errorState.activeExistsGoToNumbered", { returnNumber }) : t("errorState.activeExistsGoTo"),
      onClick: onGoToReturn,
      variant: "filled",
    });
  }
  if (onCancel) actions.push({ label: t("errorState.activeExistsCancel"), onClick: onCancel, variant: "default" });
  const detail = [productName, returnNumber].filter(Boolean).join(" · ");
  return (
    <ReturnErrorState
      color="orange"
      title={t("errorState.activeExistsTitle")}
      message={
        detail
          ? t("errorState.activeExistsMessageDetail", { detail })
          : t("errorState.activeExistsMessage")
      }
      actions={actions}
    />
  );
}

// RET-011 — optimistic-lock conflict; the row was updated elsewhere (§24).
export function ReturnOptimisticLockError({ onRefresh }: { onRefresh?: () => void }) {
  const { t } = useTranslation("buyerApp");
  return (
    <ReturnErrorState
      icon={HourglassMediumIcon}
      color="yellow"
      title={t("errorState.lockTitle")}
      message={t("errorState.lockMessage")}
      actions={onRefresh ? [{ label: t("errorState.lockRefresh"), onClick: onRefresh, variant: "filled" }] : []}
    />
  );
}

// RET-110 — permission denied for the attempted action (§24).
export function ReturnPermissionError({
  requiredPermission,
  onBack,
}: {
  requiredPermission?: string;
  onBack?: () => void;
}) {
  const { t } = useTranslation("buyerApp");
  return (
    <ReturnErrorState
      icon={LockKeyIcon}
      color="gray"
      title={t("errorState.permissionTitle")}
      message={
        requiredPermission
          ? t("errorState.permissionMessageDetail", { permission: requiredPermission })
          : t("errorState.permissionMessage")
      }
      actions={onBack ? [{ label: t("errorState.permissionBack"), onClick: onBack, variant: "default" }] : []}
    />
  );
}
